/**
 * PropertyPhotoGalleryModal.tsx
 *
 * Drag-and-drop gallery manager for property photos.
 * - Loads all images (id, url, position) from GET /properties/:id/gallery-images
 * - Allows drag-and-drop reordering (native HTML5 DnD)
 * - Upload new photos (resized client-side)
 * - Delete individual photos
 * - Cover photo = item at position 0 (shown with a badge)
 */

import * as documentApi from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.api";
import { IFile } from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.types";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  EmptyState,
  Modal,
} from "@rentiohq/web-shared/dist/components";
import React, { useEffect, useRef, useState } from "react";
import { resizeImageFile } from "../../../../../utils/imageResize.utils";
import styles from "./PropertyPhotoGalleryModal.module.css";

interface IPropertyImageItem {
  id: number;
  imageDocumentId: string | null;
  position: number;
  url: string | null;
  isOptimistic?: boolean;
}

interface IPropertyPhotoGalleryModalProps {
  propertyId: number;
  onClose: () => void;
  onSaved: () => void;
}

export const PropertyPhotoGalleryModal: React.FC<
  IPropertyPhotoGalleryModalProps
> = ({ propertyId, onClose, onSaved }) => {
  const [images, setImages] = useState<IPropertyImageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag state
  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Load images on mount
  useEffect(() => {
    loadImages();
  }, [propertyId]);

  const loadImages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await api.get(`/properties/${propertyId}/gallery-images`, {
        isV2: true,
      });
      setImages((result as any).data?.data || []);
    } catch (err) {
      setError(getLocalizedText("property.gallery.error.load"));
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Native drag-and-drop ──────────────────────────────────────────────────

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const fromIndex = dragIndex.current;
    if (fromIndex === null || fromIndex === dropIndex) {
      setDragOverIndex(null);
      dragIndex.current = null;
      return;
    }

    const reordered = Array.from(images);
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, removed);

    setImages(reordered);
    setIsDirty(true);
    setDragOverIndex(null);
    dragIndex.current = null;
  };

  const handleDragEnd = () => {
    setDragOverIndex(null);
    dragIndex.current = null;
  };

  // ─── Save order ───────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!isDirty) {
      onClose();
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      await api.post(`/properties/${propertyId}/gallery-reorder`, {
        data: { imageIds: images.map(img => img.id) },
        isV2: true,
      });
      setIsDirty(false);
      onSaved();
      onClose();
    } catch (err) {
      setError(getLocalizedText("property.gallery.error.save"));
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Upload ───────────────────────────────────────────────────────────────

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Reset file input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = "";

    setIsUploading(true);
    setError(null);
    setUploadProgress({ current: 0, total: files.length });

    const localPreviews: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ current: i + 1, total: files.length });
        const resized = await resizeImageFile(files[i]);

        // Show local blob URL immediately so there's no broken image while the
        // Lambda generates the thumbnail.
        const localUrl = URL.createObjectURL(resized as unknown as Blob);
        localPreviews.push(localUrl);
        setImages(prev => [
          ...prev,
          {
            id: Date.now() + i,
            imageDocumentId: null,
            position: prev.length + i,
            url: localUrl,
            isOptimistic: true,
          },
        ]);

        const preSignedUrl = await documentApi.uploadDocumentToS3(
          resized as unknown as IFile,
        );
        await api.post(`/properties/${propertyId}/gallery-upload`, {
          data: {
            preSignedUrl,
            filename: resized.name,
          },
          isV2: true,
        });
      }
      await loadImages();
      localPreviews.forEach(url => URL.revokeObjectURL(url));
      setIsDirty(false); // fresh load — no pending reorder
      onSaved(); // notify parent (e.g. refresh cover image)
    } catch (err) {
      localPreviews.forEach(url => URL.revokeObjectURL(url));
      setError(getLocalizedText("property.gallery.error.upload"));
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = async (imageId: number) => {
    confirm({
      title: getLocalizedText("property.gallery.delete_photo"),
      info: getLocalizedText("property.gallery.delete_confirm"),
      primaryActions: [
        {
          title: getLocalizedText("system.delete"),
          onPress: async () => {
            try {
              setError(null);
              await api.post(`/properties/${propertyId}/gallery-delete`, {
                data: { imageId },
                isV2: true,
              });
              // Optimistic update
              setImages(prev => prev.filter(img => img.id !== imageId));
              setIsDirty(false);
              onSaved();
            } catch (err) {
              setError(getLocalizedText("property.gallery.error.delete"));
            }
          },
        },
      ],
    });
  };

  // ─── Upload button label ───────────────────────────────────────────────────

  const getUploadLabel = () => {
    if (!isUploading) return getLocalizedText("property.gallery.upload_photos");
    if (uploadProgress && uploadProgress.total > 1) {
      return `${getLocalizedText("system.loading")} (${
        uploadProgress.current
      }/${uploadProgress.total})`;
    }
    return `${getLocalizedText("system.loading")}…`;
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const saveButtonLabel = isSaving
    ? getLocalizedText("system.loading")
    : getLocalizedText(isDirty ? "system.save" : "system.close");

  const modalActions = [
    {
      content: getLocalizedText("system.cancel"),
      onClick: onClose,
    },
    {
      content: saveButtonLabel,
      onClick: handleSave,
      appearance: isDirty ? ("primary" as const) : ("outline" as const),
      isDisabled: isSaving || isUploading,
    },
  ];

  return (
    <Modal
      heading={getLocalizedText("property.gallery.modal.title")}
      onClose={onClose}
      actions={modalActions}
    >
      <div className={styles.container}>
        {/* Error banner — Rentio Banner component */}
        {error && (
          <div className={styles.errorBannerWrapper}>
            <Banner variation="error" title={error} hasDismiss />
          </div>
        )}

        {/* Upload button row */}
        <div className={styles.uploadArea}>
          <Button
            appearance="outline"
            isSubmitting={isUploading}
            isDisabled={isUploading}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            {getUploadLabel()}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <p className={styles.hint}>
            {getLocalizedText("property.gallery.drag_hint")}
          </p>
        </div>

        {/* Loading state — Rentio EmptyState with isLoading */}
        {isLoading ? <EmptyState isLoading size="small" /> : null}

        {/* Empty state — Rentio EmptyState component */}
        {!isLoading && images.length === 0 ? (
          <EmptyState
            icon="house"
            heading={getLocalizedText("property.gallery.empty")}
            action={{
              content: getLocalizedText("property.gallery.upload_photos"),
              onClick: () => fileInputRef.current?.click(),
            }}
          >
            {getLocalizedText("property.gallery.drag_hint")}
          </EmptyState>
        ) : null}

        {/* Gallery grid */}
        {!isLoading && images.length > 0 ? (
          <div className={styles.gridWrapper}>
            <div className={styles.grid}>
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className={`${styles.card} ${
                    dragOverIndex === index ? styles.cardDragOver : ""
                  }`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={e => handleDragOver(e, index)}
                  onDrop={e => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  {/* Cover badge */}
                  {index === 0 && (
                    <div className={styles.coverBadge}>
                      {getLocalizedText("property.gallery.cover_photo")}
                    </div>
                  )}

                  {/* Image */}
                  <div className={styles.imageWrapper}>
                    {image.url ? (
                      <img
                        src={image.url}
                        alt={`${index + 1}`}
                        className={styles.image}
                      />
                    ) : (
                      <div className={styles.imagePlaceholder} />
                    )}
                  </div>

                  {/* Delete — Rentio Button appearance="danger" */}
                  <Button
                    appearance="danger"
                    size="slim"
                    onClick={e => {
                      (e as any).stopPropagation?.();
                      handleDelete(image.id);
                    }}
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      minWidth: "unset",
                      padding: "2px 6px",
                    }}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
